import gender from 'gender-detection';

export type GenderResult = 'male' | 'female' | 'unknown';
export type GenderLanguage = 'en' | 'it' | 'es' | 'fr' | 'de';

export interface GenderEstimate {
  gender: GenderResult;
  confidence: number;
  source: 'honorific' | 'name_detection';
  sourceDetail: string;
}

// Names that are commonly used for any gender - override library result to 'unknown'
// The gender-detection library only marks names as 'unisex' if they appear in BOTH
// male and female datasets. Many real-world unisex names only appear in one dataset,
// causing incorrect gender assignments (e.g., "Kelly" returns "female").
const UNISEX_NAMES = new Set([
  'kelly',
  'taylor',
  'jordan',
  'casey',
  'morgan',
  'alex',
  'jamie',
  'riley',
  'avery',
  'quinn',
  'drew',
  'blair',
  'cameron',
  'peyton',
  'reese',
  'skyler',
  'finley',
  'rowan',
  'emery',
  'parker',
  'sawyer',
  'hayden',
  'kennedy',
  'logan',
  'dakota',
  'river',
  'sage',
  'phoenix',
  'jessie',
  'jackie',
  'shannon',
  'leslie',
  'robin',
  'tracy',
  'terry',
  'lee',
  'kim',
  'pat',
  'chris',
  'jan',
  'jean',
  'francis',
  'gene',
  'lynn',
  'stacy',
  'dana',
  'shawn',
  'jody',
  'sandy',
  'kerry',
  'mickey',
  'rene',
  'rory',
  'stevie',
  'angel',
  'cody',
  'dylan',
  'aubrey',
  'devon',
  'harley',
  'shelby',
  'sidney',
  'marley',
  'ariel',
  'toby',
  'ezra',
  'charlie',
  'frankie',
  'billie',
  'bobbie',
  'johnnie',
  'willie',
  'eddie',
  'tommie',
  'lonnie',
  'ollie',
  'addison',
  'bailey',
  'brooklyn',
  'elliot',
  'elliott',
  'emerson',
  'justice',
  'kai',
  'london',
  'marlowe',
  'milan',
  'ocean',
  'oakley',
  'onyx',
  'payton',
  'remy',
  'royal',
  'sasha',
  'sloan',
  'sloane',
  'storm',
  'winter',
  'ashton',
  'blake',
  'dallas',
  'dallas',
  'eden',
  'gray',
  'grey',
  'harper',
  'hunter',
  'indigo',
  'jude',
  'jules',
  'kit',
  'landry',
  'lake',
  'navy',
  'noel',
  'paris',
  'reed',
  'reid',
  'remington',
  'rio',
  'sky',
  // Library misclassifies these as a single gender, but they're commonly used for any gender
  'andy',
  'sam',
  'pat',
]);

// Nicknames the gender-detection library misclassifies or fails to detect.
// Verified against gender-detection v0.1.0 — only add a name here after confirming
// gender.detect(name) returns something other than 'male'.
// Truly ambiguous nicknames (Andy, Sam, Pat, Chris, Alex) go in UNISEX_NAMES, not here.
const MALE_NAMES = new Set([
  'mike', // library returns 'female'
  'mikey', // library returns 'unknown'
  'tony',
  'nick',
  'joe',
  'bob',
  'bill',
  'tom',
  'jim',
  'rob',
  'chuck',
  'ed',
  'ted',
]);

// Common honorific to gender mapping
const HONORIFIC_GENDER_MAP: Record<string, GenderResult> = {
  'Mr.': 'male',
  'Mr': 'male',
  'Mrs.': 'female',
  'Mrs': 'female',
  'Ms.': 'female',
  'Ms': 'female',
  'Miss': 'female',
  'Miss.': 'female',
  // Dr., Prof., etc. are gender-neutral
};

/**
 * Detect gender from honorific prefix.
 * Returns high confidence (1.0) since honorifics are explicit gender indicators.
 */
export function detectGenderFromHonorific(honorific: string | null | undefined): GenderEstimate | null {
  if (!honorific) {
    return null;
  }

  const normalizedHonorific = honorific.trim();
  const gender = HONORIFIC_GENDER_MAP[normalizedHonorific];

  if (gender) {
    return {
      gender,
      confidence: 1.0,
      source: 'honorific' as const,
      sourceDetail: `honorific: ${normalizedHonorific}`,
    };
  }

  return null;
}

/**
 * Detect gender from first name using the gender-detection library.
 * Falls back to unknown if name cannot be determined.
 * If the full name returns unknown and contains spaces, tries just the first word.
 * @param firstName - The first name to detect gender from
 * @param lang - Language for name detection (kept for API compatibility, not used by current library)
 */
export function detectGenderFromName(
  firstName: string | null | undefined,
  _lang: GenderLanguage = 'en',
): GenderEstimate | null {
  if (!firstName?.trim()) {
    return null;
  }

  const normalizedName = firstName.trim();

  // Check our unisex override list first (use first word for compound names)
  const firstWord = normalizedName.split(' ')[0] ?? normalizedName;
  const firstWordLower = firstWord.toLowerCase();
  if (UNISEX_NAMES.has(firstWordLower)) {
    return {
      gender: 'unknown' as const,
      confidence: 0,
      source: 'name_detection' as const,
      sourceDetail: `first_name: ${normalizedName} (unisex)`,
    };
  }

  // Check male override list (library misclassifies some common nicknames)
  if (MALE_NAMES.has(firstWordLower)) {
    return {
      gender: 'male' as const,
      confidence: 1.0,
      source: 'name_detection' as const,
      sourceDetail: `first_name: ${normalizedName} (override)`,
    };
  }

  let rawResult = gender.detect(normalizedName) as string | null;
  // Map 'unisex' to 'unknown' - we prefer unknown over guessing
  let result: GenderResult | null = rawResult === 'unisex' ? 'unknown' : (rawResult as GenderResult | null);

  // If unknown and name contains spaces, try just the first word (e.g., "Mary Anne" -> "Mary")
  if ((!result || result === 'unknown') && normalizedName.includes(' ')) {
    const firstWord = normalizedName.split(' ')[0];
    if (firstWord) {
      rawResult = gender.detect(firstWord) as string | null;
      result = rawResult === 'unisex' ? 'unknown' : (rawResult as GenderResult | null);
    }
  }

  if (!result || result === 'unknown') {
    return {
      gender: 'unknown' as const,
      confidence: 0,
      source: 'name_detection' as const,
      sourceDetail: `first_name: ${normalizedName}`,
    };
  }

  return {
    gender: result,
    confidence: 0.8, // Library doesn't return confidence, use default
    source: 'name_detection' as const,
    sourceDetail: `first_name: ${normalizedName}`,
  };
}

/**
 * Combined gender detection: tries honorific first (highest confidence), then name detection.
 * @param firstName - The first name to detect gender from
 * @param honorific - Optional honorific prefix (Mr., Mrs., etc.)
 * @param lang - Language for name detection (default: 'en')
 */
export function detectGender(
  firstName: string | null | undefined,
  honorific: string | null | undefined,
  lang: GenderLanguage = 'en',
): GenderEstimate | null {
  // Try honorific first (highest confidence)
  const honorificResult = detectGenderFromHonorific(honorific);
  if (honorificResult) {
    return honorificResult;
  }

  // Fall back to name detection
  return detectGenderFromName(firstName, lang);
}

/**
 * Determines if an honorific should be displayed based on:
 * - If it's "Dr." - always show regardless of age
 * - For other honorifics (Mr., Ms., Mrs.) - only show if age meets threshold
 */
export function shouldShowHonorific(
  honorificTitle: string | null | undefined,
  minAge: number | null | undefined,
  honorificAgeThreshold: number | null | undefined,
): boolean {
  if (!honorificTitle) return false;

  // Always show Dr. regardless of age
  const isDrHonorific = honorificTitle.toLowerCase().startsWith('dr');
  if (isDrHonorific) return true;

  // For other honorifics, check age threshold
  return honorificAgeThreshold != null && minAge != null && minAge >= honorificAgeThreshold;
}
